import { Fragment, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';

import { HooksService, Spreadsheet, SpreadsheetContextMenu, SpreadsheetUtils, WithTooltip } from 'basic-components';
import { globalActions } from 'primary-components';

import { csvEditorActions } from 'actions/modules/tools';

import Papa from 'papaparse';

const getColumnWidth = (content, columnId, columnName) => {
  const firstRowCol = content[1]?.[columnId]?.length * 8 + 40
  const headerCol = columnName?.length * 8 + 60
  let toReturn =  firstRowCol > headerCol ? firstRowCol : headerCol
  toReturn = toReturn > 600 ? 600 : toReturn
  toReturn = toReturn < 80 ? 80 : toReturn
  return toReturn
}

const prepareColumns = (columns, content) => columns?.map(c => {
  const width = getColumnWidth(content, c.columnId, c.columnName)
  return ({ columnName: c.columnName, width, columnId: c.columnId })
})

const headerRow = (columns = []) => SpreadsheetUtils.getDecoratedSpreadsheetHeaderRow({
  cells: columns.map(column => SpreadsheetUtils.getDecoratedSpreadsheetHeaderCell({ 
    text: column.columnName,
    textAlign: column.textAlign
  }))
});

const getRows = (data, columns, editable, errorCells, getCellStyle) => [
  headerRow(columns),
  ...data.map((data, idx) => SpreadsheetUtils.getDecoratedSpreadsheetRow({
    rowId: idx,
    cells: (columns || []).map((column, columnIdx) => {
      const errorCell = errorCells.find(e => e.columnNumber === columnIdx && e.rowNumber === idx)
      return SpreadsheetUtils.getDecoratedSpreadsheetCell({ 
        style: getCellStyle?.({ rowNumber: idx, columnId: column.columnId }),
        text: data[column.columnId],
        nonEditable: !editable,
        validator: () => !errorCell,
        renderer: text => (
          <Fragment>
            <WithTooltip
                color="red-light"
                text={errorCell ? errorCell?.message : ""}
            >
              <div style={{
                ...SpreadsheetUtils.defaultCellStyle,
                justifyContent: SpreadsheetUtils.textAlignToJustifyContent(column.textAlign)
              }}>
                {text}
              </div>
            </WithTooltip>
          </Fragment>
        )
      })
    })
  }))
];

const SELECTION_MODE = SpreadsheetUtils.SELECTION_MODE
const EMPTY_ARRAY = [];
const Worksheet = ({
  className = "",
  editable = false,
  errorCells = EMPTY_ARRAY,
  getCellStyle = undefined,
  getHeaderContextMenu = undefined,
  onLoadEnd = undefined,
  ref,
  ...props
}) => {
  const dispatch = useDispatch(),
        [data, setData] = useState([]),
        [columns, setColumns] = useState([]),
        rows = useMemo(() => columns?.length !== 0 ? getRows(data, columns, editable, errorCells, getCellStyle) : undefined, [data, columns, editable, errorCells, getCellStyle]),
        rowsLength = useMemo(() => rows?.length, [rows?.length]),
        prevRowsLength = HooksService.usePrevious(rowsLength),
        divRef = useRef(),
        spreadSheetRef = useRef(),
        onRowsPasteWhenFiltered = useCallback(() => dispatch(globalActions.getMessageStore()).warning("Pasting multiple rows is unavailable while filtering is active. Clear the filter and try again."), [dispatch]),
        simpleHandleContextMenu = useCallback(({ cells, selectionMode, ...props }) => {
          if(cells.length === 0) {
            return []
          }
          const isHeaderSelected = String(cells[0]?.[0]?.rowId).startsWith("header")
          if(isHeaderSelected || selectionMode === SELECTION_MODE.COLUMN) {
            return SpreadsheetContextMenu.getDefaultHeaderItems({ cells, selectionMode, ...props }).concat(getHeaderContextMenu?.({ cells , ...props }) || [])
          } else if(selectionMode === SELECTION_MODE.RANGE || selectionMode === SELECTION_MODE.ROW) {
            return SpreadsheetContextMenu.getDefaultItems({ cells, selectionMode, ...props })
          }
        }, [getHeaderContextMenu])
  

  useImperativeHandle(ref, () => ({
    getAsCSV: () => {
      if(columns.length === 0) {
        return "[]";
      }
      const headersRow = Papa.unparse({
        fields: columns.map(column => column.columnName),
        data: []
      })
      const unparsedCsv = Papa.unparse(data, { 
        columns: columns.map(column => column?.columnId),
        header: false
      })
    return headersRow + unparsedCsv
    },
    loadFromCSV: (content, onComplete) => {
      setTimeout(() => {
        Papa.parse(content, {
          complete: result => {
            setData(result.data)
            setColumns(SpreadsheetUtils.getDecoratedSpreadsheetColumns(prepareColumns(result?.meta?.fields || [], result.data)))
            onComplete?.()
          },
          skipEmptyLines: true,
          worker: true,
          header: true
        })
      }, 1)
    },
    ...spreadSheetRef.current,
    setData,
    getData: () => data,
    setColumns: ({ data, columnsArray = Object.keys(data?.[0] || {}) }) => {
      setColumns(SpreadsheetUtils.getDecoratedSpreadsheetColumns(prepareColumns(columnsArray, data)))
    },
    getColumns: () => columns,
    getRows: () => rows,
    setEmptyTable: () => csvEditorActions.setEmptyTable(setColumns, setData)
  }), [columns, data, rows])

  useEffect(() => {
    if(rowsLength !== undefined && prevRowsLength !== rowsLength && prevRowsLength === undefined) {
      onLoadEnd?.()
    }
  }, [onLoadEnd, prevRowsLength, rowsLength])

  return (
    <div 
        className={className}
        ref={divRef}
    >
      {columns.length > 0 && (
        <Spreadsheet
            areColumnsEditable
            columns={columns}
            containerRef={divRef}
            editable={editable}
            headerRowsCount={1}
            onContextMenu={simpleHandleContextMenu}
            onRowsPasteWhenFiltered={onRowsPasteWhenFiltered}
            ref={spreadSheetRef}
            rows={rows}
            setColumns={setColumns}
            setData={setData}
            
            {...props}
        />
      )}
    </div>
  )
}

Worksheet.displayName="Worksheet"

Worksheet.propTypes = {
  className: PropTypes.string,
  editable: PropTypes.bool,
  errorCells: PropTypes.array,
  getCellStyle: PropTypes.func,
  getHeaderContextMenu: PropTypes.func,
  onLoadEnd: PropTypes.func
}

export default Worksheet;
